import { Action, Mutation, Module, VuexModule } from "vuex-module-decorators";
import { Actions, Mutations } from "@/store/enums/StoreEnums";
import AdminNotificationsService from "@/core/services/AdminNotificationsService";
import type {
  AdminNotificationRecord,
  AdminNotificationsHistoryResponse,
  CreateAdminNotificationPayload,
} from "@/core/services/AdminNotificationsService";

/**
 * El backend (Seccuri.Shared.Results.Result<T>) serializa los errores como
 * `errors: [{ error: string, code: string | null }]`, no como strings
 * sueltos — ver misma nota en SkillsSimulationModule.ts.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function extractErrorMessages(response: any, fallback: string): Array<string> {
  const errors = response?.data?.errors;
  if (Array.isArray(errors) && errors.length) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return errors.map((e: any) => e?.error ?? e?.message ?? String(e));
  }
  return [fallback];
}

/**
 * Modulo de administracion de notificaciones (compania).
 *
 * Exclusivo de la cuenta contact@seccuri.com (la "principal" de Seccuri) —
 * la restriccion de UI vive en el router (meta.superAdminOnly, ver
 * router/index.ts) y en el menu (MainMenuCompanyConfig.ts + Menu.vue),
 * pero la autorizacion real debe reforzarse en el backend: el endpoint
 * POST /admin-notifications no deberia confiar solo en el frontend.
 *
 * Permite crear notificaciones que se envian a todos los Talent y ver el
 * historial de lo enviado (alcance confirmado con Andres: crear + ver
 * historial, sin editar/eliminar). El backend AUN NO EXISTE — ver
 * AdminNotificationsService.ts para el contrato propuesto. Mientras tanto
 * las acciones fallan de forma segura (mismo patron que
 * SkillsSimulationModule): loading se apaga, se guarda el mensaje de
 * error y la vista puede mostrarlo sin romperse.
 *
 * Las llamadas HTTP viven en AdminNotificationsService (no se llama a
 * ApiService directamente desde aqui).
 */

interface AdminNotificationsState {
  history: Array<AdminNotificationRecord>;
  total: number;
  loading: boolean;
  errors: Array<string>;
}

@Module
export default class AdminNotificationsModule
  extends VuexModule
  implements AdminNotificationsState
{
  history: Array<AdminNotificationRecord> = [];
  total = 0;
  loading = false;
  errors: Array<string> = [];

  get adminNotificationsHistory(): Array<AdminNotificationRecord> {
    return this.history;
  }

  get adminNotificationsTotal(): number {
    return this.total;
  }

  get adminNotificationsLoading(): boolean {
    return this.loading;
  }

  get adminNotificationsErrors(): Array<string> {
    return this.errors;
  }

  @Mutation
  [Mutations.SET_ADMIN_NOTIFICATIONS_LOADING_MUTATION](payload: boolean) {
    this.loading = payload;
  }

  @Mutation
  [Mutations.SET_ADMIN_NOTIFICATIONS_ERROR_MUTATION](payload: Array<string>) {
    this.errors = payload;
  }

  @Mutation
  [Mutations.SET_ADMIN_NOTIFICATIONS_HISTORY_MUTATION](
    payload: AdminNotificationsHistoryResponse,
  ) {
    this.history = payload.items;
    this.total = payload.total;
  }

  @Mutation
  [Mutations.ADD_ADMIN_NOTIFICATION_MUTATION](
    payload: AdminNotificationRecord,
  ) {
    this.history.unshift(payload);
    this.total += 1;
  }

  @Action
  [Actions.CREATE_ADMIN_NOTIFICATION](payload: CreateAdminNotificationPayload) {
    this.context.commit(
      Mutations.SET_ADMIN_NOTIFICATIONS_LOADING_MUTATION,
      true,
    );
    this.context.commit(Mutations.SET_ADMIN_NOTIFICATIONS_ERROR_MUTATION, []);
    return AdminNotificationsService.create(payload)
      .then(({ data }) => {
        this.context.commit(
          Mutations.ADD_ADMIN_NOTIFICATION_MUTATION,
          data.data,
        );
        return data.data as AdminNotificationRecord;
      })
      .catch(({ response }) => {
        this.context.commit(
          Mutations.SET_ADMIN_NOTIFICATIONS_ERROR_MUTATION,
          extractErrorMessages(
            response,
            "No se pudo enviar la notificacion. Intente de nuevo.",
          ),
        );
        return undefined;
      })
      .finally(() => {
        this.context.commit(
          Mutations.SET_ADMIN_NOTIFICATIONS_LOADING_MUTATION,
          false,
        );
      });
  }

  @Action
  [Actions.GET_ADMIN_NOTIFICATIONS_HISTORY](payload?: {
    page?: number;
    pageSize?: number;
  }) {
    this.context.commit(
      Mutations.SET_ADMIN_NOTIFICATIONS_LOADING_MUTATION,
      true,
    );
    return AdminNotificationsService.getHistory(
      payload?.page,
      payload?.pageSize,
    )
      .then(({ data }) => {
        const response = data.data as AdminNotificationsHistoryResponse;
        this.context.commit(
          Mutations.SET_ADMIN_NOTIFICATIONS_HISTORY_MUTATION,
          {
            items: response?.items ?? [],
            total: response?.total ?? 0,
          },
        );
        return response;
      })
      .catch(({ response }) => {
        this.context.commit(
          Mutations.SET_ADMIN_NOTIFICATIONS_ERROR_MUTATION,
          extractErrorMessages(
            response,
            "No se pudo consultar el historial de notificaciones.",
          ),
        );
        return undefined;
      })
      .finally(() => {
        this.context.commit(
          Mutations.SET_ADMIN_NOTIFICATIONS_LOADING_MUTATION,
          false,
        );
      });
  }
}
